'use client' // Directive for Next.js indicating this is a client-side module

// Import required hooks and types from Formik and Yup for form handling and validation
import { useFormik, FormikHelpers, FormikValues } from 'formik'
import * as Yup from 'yup'

// Define a generic interface for configuring the custom Formik hook
export interface UseCustomFormikOptions<T extends FormikValues> {
  initialValues: T // Initial form field values
  validationSchema?: Yup.ObjectSchema<T> // Optional Yup validation schema
  validate?: (values: T) => Partial<Record<keyof T, string>> | void // Optional custom validation function
  onSubmit: (values: T, formikHelpers: FormikHelpers<T>) => void | Promise<void> // Submit handler
  validateOnBlur?: boolean // Run validation when a field loses focus
  validateOnChange?: boolean // Run validation on every field change
  enableReinitialize?: boolean // Reinitialize form when `initialValues` change
}

// Custom reusable Formik hook for managing form state and logic
function useCustomFormik<T extends FormikValues>({
  initialValues,
  validationSchema,
  validate,
  onSubmit,
  validateOnBlur = true,
  validateOnChange = true,
  enableReinitialize = false,
}: UseCustomFormikOptions<T>) {
  return useFormik<T>({
    initialValues, // Set initial values for the form
    validationSchema, // Optional schema-based validation using Yup
    validate, // Optional manual validation logic
    onSubmit, // Function to handle form submission
    validateOnBlur, // Enable or disable validation on blur
    validateOnChange, // Enable or disable validation on change
    enableReinitialize, // Allow reinitializing form if `initialValues` change
  })
}

export default useCustomFormik // Export the hook for use in other components
